import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { TableauUserOrderField } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createSelectionColumn,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  FeatureFlagsContext,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  UnsortableColumnKey,
  UserAndSiteSettingsContext,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import { OwnersValidSortFields } from '../sorting/sortProviders/OwnersGridSortProvider';

export interface OwnersDisplayGridRowData {
  readonly id: string;
  readonly name: string | null;
  readonly username: string | null;
  readonly domain: string | null;
  readonly workbookCount: number | null;
  readonly datasourceCount: number | null;
  readonly metricsCount: number | null;
  readonly virtualConnectionsCount: number | null;
}

type OwnersColumnFactory = ColumnFactory<
  GridSortProps<OwnersValidSortFields> & { readonly areUsersVisible: boolean },
  OwnersDisplayGridRowData,
  OwnersValidSortFields
>;

function memoizedGetColumns(metricsDataCatalogEnabled: boolean, publishedConnectionsLineageEnabled: boolean): ReadonlyArray<OwnersColumnFactory> {
  const ownedMetricsColumn: OwnersColumnFactory = (sortParams) => ({
    name: 'ownedMetrics',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderOwnedMetrics(),
    headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
    cellFormatter: createNumberCellFormatter(({ metricsCount }) => metricsCount),
    resizable: true,
    sortable: false,
  });

  const ownedVirtualConnectionsColumn: OwnersColumnFactory = (sortParams) => ({
    name: 'ownedVirtualConnections',
    dataKey: UnsortableColumnKey,
    headerText: Messages.GridHeaderOwnedVirtualConnections(),
    headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
    cellFormatter: createNumberCellFormatter(({ virtualConnectionsCount }) => virtualConnectionsCount),
    resizable: true,
    sortable: false,
  });

  return [
    () => createSelectionColumn(),
    (sortParams) => ({
      name: 'name',
      dataKey: TableauUserOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createUserNameLinkCellFormatter(user => user),
      resizable: true,
      sortable: sortParams.areUsersVisible,
    }),
    (sortParams) => ({
      name: 'ownedWorkbooks',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderOwnedWorkbooks(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter(({ workbookCount }) => workbookCount),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: 'ownedDatasources',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderOwnedDatasources(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter(({ datasourceCount }) => datasourceCount),
      resizable: true,
      sortable: false,
    }),
    ...(metricsDataCatalogEnabled ? [ownedMetricsColumn] : []),
    ...(publishedConnectionsLineageEnabled ? [ownedVirtualConnectionsColumn] : []),
  ];
}


export type OwnersDisplayGridProps =
  UsualDisplayGridProps<OwnersDisplayGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<OwnersValidSortFields>;

export function OwnersDisplayGrid(props: OwnersDisplayGridProps): JSX.Element {
  const { areUsersVisible } = React.useContext(UserAndSiteSettingsContext);

  return (
    <FeatureFlagsContext.Consumer>
      {
        featureFlags =>
          <ExplorerStyledDisplayGrid
            columns={memoizedGetColumns(featureFlags.metricsDataCatalogEnabled, featureFlags.publishedConnectionsLineageEnabled)
              .map(columnFactory => columnFactory({ ...props, areUsersVisible }))}
            rowStore={props.rowStore}
            selectedKeys={props.selectedKeys}
            updateSelectionForKey={props.updateSelectionForKey}
            emptyGridMessage={props.emptyGridMessage}
            rowCount={props.rowCount}
            renderErrors={reportGraphQLErrors}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
      }
    </FeatureFlagsContext.Consumer>
  );
}
